import { PlusIcon, X } from '../../../assets/svg';
import { ProviderConnectionType } from '../../../types/api/location.types';
import { PayoutProviderRule } from '../../../types/location.types';
import { Provider, ProviderType } from '../../../types/shared.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { Div, Icon } from '../../../styles/styles';
import { H5, P } from '../../../styles/text';
import { lightTheme } from '../../../styles/theme';
import {
  PAYOUT_RULE_PROVIDERS,
  PAYOUT_RULE_SOURCES,
  ProviderRuleAllowance,
  createEmptyProviderRule,
  payoutSourceLabel,
  providersClaimedByOtherRules,
  sourcesForProviders,
} from '../payoutProviderRules';
import { Button } from '../../../components/button/button';
import { AdminCustomProviderSelect } from '../../AdminCustomProviderSelect/AdminCustomProviderSelect';
import { AdminCustomSelect } from '../../AdminCustomSelect/AdminCustomSelect';
import { MoneyInput, PercentageInput } from './PayoutNumberInputs';
import { GroupCard, GroupDivider, SectionNote } from './payoutSections.styled';

const SOURCE_OPTIONS = PAYOUT_RULE_SOURCES.map((source) => ({
  name: payoutSourceLabel(source),
  value: source,
}));

/**
 * The shared select is typed against `Provider`, the narrower enum the rest of
 * the admin works with. Payout rules accept every value the backend knows
 * (`ProviderType`) and the two are the same strings, so they meet with a cast
 * here rather than by widening `Provider` - that would drop these providers
 * into every provider dropdown in the app.
 */
const asProviders = (providers: ProviderType[]) =>
  providers as unknown as Provider[];

const toProviderTypes = (value: unknown): ProviderType[] =>
  (Array.isArray(value) ? value : value ? [value] : []) as ProviderType[];

const toSources = (value: unknown): ProviderConnectionType[] =>
  (Array.isArray(value)
    ? value
    : value
    ? [value]
    : []) as ProviderConnectionType[];

type Props = {
  rules: PayoutProviderRule[];
  onChange: (rules: PayoutProviderRule[]) => void;
  /** Which values this level's model lets an exception carry. */
  allow: ProviderRuleAllowance;
  title?: string;
  /** Extra line under the standard explanation, for level specific caveats. */
  note?: string;
  /** Draws the note in red - for a state the admin has to act on. */
  noteIsWarning?: boolean;
  /**
   * Rendered inside an already-bordered group (a tier): tinted instead of
   * bordered, and with the short explanation - the full one repeated in
   * every tier buries the fields it explains.
   */
  nested?: boolean;
  disabled?: boolean;
};

const RuleRow = ({
  rule,
  index,
  unavailable,
  allow,
  onChange,
  onRemove,
  disabled,
}: {
  rule: PayoutProviderRule;
  index: number;
  unavailable: PayoutProviderRule['providers'];
  allow: ProviderRuleAllowance;
  onChange: (patch: Partial<PayoutProviderRule>) => void;
  onRemove: () => void;
  disabled?: boolean;
}) => {
  // Only the sources these providers can actually be connected over. Picking
  // providers therefore rewrites this field, and a source that no longer
  // applies is dropped rather than saved as a rule no order can match.
  const sources = sourcesForProviders(rule.providers);

  const changeProviders = (next: unknown) => {
    const providers = toProviderTypes(next);
    const allowed = sourcesForProviders(providers);

    onChange({
      providers,
      sources: rule.sources.filter((source) => allowed.includes(source)),
    });
  };

  return (
    <Div $flex $column $gap="8px" $p="4px 0 0 0">
      <Div $flex $alignCenter $between>
        <P $bold $m="0">
          Exception {index + 1}
        </P>
        {!disabled && (
          <Icon $pointer $size="12" onClick={onRemove}>
            <X fill={lightTheme.darkGray} />
          </Icon>
        )}
      </Div>

      {/* Read-only views have no disabled state on the shared select, so the
        whole control is taken out of reach instead. */}
      <Div
        style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
      >
        <AdminCustomProviderSelect
          multiSelect
          label="Providers"
          placeholder="Select providers"
          emptyPlaceholder="Every provider is already used by another exception for these order sources."
          $shadowDesign={false}
          providerArray={asProviders(PAYOUT_RULE_PROVIDERS)}
          disabledProviders={asProviders(unavailable)}
          value={asProviders(rule.providers)}
          onChange={changeProviders}
        />
      </Div>

      <Div
        style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
      >
        <AdminCustomSelect<ProviderConnectionType>
          multiSelect
          label="Order Source"
          placeholder="All sources"
          emptyPlaceholder="Pick a provider first."
          $shadowDesign={false}
          options={SOURCE_OPTIONS}
          disabledOptions={PAYOUT_RULE_SOURCES.filter(
            (source) => !sources.includes(source)
          )}
          value={rule.sources ?? []}
          onChange={(next) => onChange({ sources: toSources(next) })}
        />
      </Div>

      <Div $flex $columnSM $gap="12px">
        {allow.percentage && (
          <PercentageInput
            label="Bracket Percentage"
            value={rule.percentage}
            onChange={(percentage) => onChange({ percentage })}
            disabled={disabled}
          />
        )}
        {allow.flatFee && (
          <MoneyInput
            label="vGrubs Flat Fee"
            value={rule.flatFee}
            onChange={(flatFee) => onChange({ flatFee })}
            disabled={disabled}
          />
        )}
      </Div>
    </Div>
  );
};

/**
 * The per-provider exceptions of one level - a config or a single tier. Only
 * rendered for the `vGrubs` service, the one whose orders come from more than
 * one provider.
 */
export const ProviderRulesSection = ({
  rules,
  onChange,
  allow,
  title = 'Provider Exceptions',
  note,
  noteIsWarning,
  nested,
  disabled,
}: Props) => {
  // Nothing to say on a read-only view with no exceptions.
  if (disabled && !rules.length) return null;

  const updateRule = (index: number, patch: Partial<PayoutProviderRule>) =>
    onChange(
      rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))
    );

  return (
    <GroupCard $nested={nested}>
      <Div>
        <H5 $m="0" $mb="4">
          {title}
        </H5>
        <SectionNote>
          Optional. Charge some providers differently
          {nested ? ' for orders in this range' : ''}. An exception{' '}
          <b>replaces</b> the values above for the providers you pick, it is not
          added to them - leave a field empty and they are not charged it at
          all. Pick an order source to narrow it to the orders that came in that
          way, e.g. Uber Eats over FoodHub; leave it on "All sources" and it
          covers every connection. Every other provider and source keeps the
          values above.
        </SectionNote>
        {note && <SectionNote $warning={noteIsWarning}>{note}</SectionNote>}
      </Div>

      {rules.map((rule, index) => (
        <div key={index}>
          {index > 0 && <GroupDivider />}
          <RuleRow
            rule={rule}
            index={index}
            allow={allow}
            disabled={disabled}
            unavailable={providersClaimedByOtherRules(rules, index)}
            onChange={(patch) => updateRule(index, patch)}
            onRemove={() => onChange(rules.filter((_, i) => i !== index))}
          />
        </div>
      ))}

      {!disabled && (
        <Div $flex $justifyCenter $mt="4">
          <Button
            $small
            $w="170px"
            $wSM="170px"
            onClick={() => onChange([...rules, createEmptyProviderRule()])}
          >
            <Div $flex $alignCenter $justifyCenter $gap="6px">
              <Icon $size="14">
                <PlusIcon />
              </Icon>
              Add Exception
            </Div>
          </Button>
        </Div>
      )}
    </GroupCard>
  );
};
